//src/services/actionPlanModels.ts
import { api } from "@/lib/apiClient";
import type { GroCrudScope } from "@/lib/groCrudScope";
import {
  createCompanyGroActionPlan,
  createCompanyGroActionPlanItem,
  getCompanyGroTemplates,
  setCompanyGroActionPlanItemStatus,
  setCompanyGroActionPlanStatus,
  updateCompanyGroActionPlan,
  updateCompanyGroActionPlanItem,
} from "@/services/modelImports";
import type {
  ActionPlanModel,
  ActionPlanModelCreateIn,
  ActionPlanModelDetail,
  ActionPlanModelItem,
  ActionPlanModelItemCreateIn,
  ActionPlanModelItemUpdateIn,
  ActionPlanModelUpdateIn,
} from "@/types/actionPlanModels";

const isCompany = (scope: GroCrudScope) => scope === "company_model";

export async function listActionPlanModels(
  params?: { q?: string; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    const q = params?.q?.trim().toLocaleLowerCase("pt-BR") ?? "";
    return data.action_plan_models.filter((item) => {
      if (typeof params?.is_active === "boolean" && item.is_active !== params.is_active) return false;
      if (!q) return true;
      return [item.code, item.title, item.description]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase("pt-BR").includes(q));
    }) as ActionPlanModel[];
  }

  return api.get<ActionPlanModel[]>("/action-plan-models", {
    params: {
      q: params?.q || undefined,
      is_active: typeof params?.is_active === "boolean" ? params.is_active : undefined,
    },
  });
}

export async function getActionPlanModel(id: string, scope: GroCrudScope = "client") {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    const item = data.action_plan_models.find((model) => model.id === id);
    if (!item) throw new Error("Plano de Ação modelo não encontrado.");
    return item as ActionPlanModelDetail;
  }
  return api.get<ActionPlanModelDetail>(`/action-plan-models/${id}`);
}

export async function createActionPlanModel(
  payload: ActionPlanModelCreateIn,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return createCompanyGroActionPlan(payload);
  return api.post<ActionPlanModel>("/action-plan-models", payload);
}

export async function updateActionPlanModel(
  id: string,
  payload: ActionPlanModelUpdateIn,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return updateCompanyGroActionPlan(id, payload);
  return api.put<ActionPlanModel>(`/action-plan-models/${id}`, payload);
}

export async function setActionPlanModelStatus(
  id: string,
  is_active: boolean,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return setCompanyGroActionPlanStatus(id, is_active);
  return api.patch<ActionPlanModel>(`/action-plan-models/${id}/status`, { is_active });
}

export async function createActionPlanModelItem(
  modelId: string,
  payload: ActionPlanModelItemCreateIn,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return createCompanyGroActionPlanItem(modelId, payload);
  return api.post<ActionPlanModelItem>(`/action-plan-models/${modelId}/items`, payload);
}

export async function updateActionPlanModelItem(
  itemId: string,
  payload: ActionPlanModelItemUpdateIn,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return updateCompanyGroActionPlanItem(itemId, payload);
  return api.put<ActionPlanModelItem>(`/action-plan-models/items/${itemId}`, payload);
}

export async function setActionPlanModelItemStatus(
  itemId: string,
  is_active: boolean,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return setCompanyGroActionPlanItemStatus(itemId, is_active);
  return api.patch<ActionPlanModelItem>(`/action-plan-models/items/${itemId}/status`, {
    is_active,
  });
}
